import React from 'react';
import ConvictionWidgetBuilder from './ConvictionWidgetBuilder';
import ConvictionWidgetRenderer from './ConvictionWidgetRenderer';
import Preview from './Preview';
import type { WidgetDefinition } from '../../../registry';
import type { ConvictionWidgetConfig, ConvictionDataContent } from './types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye } from '@fortawesome/free-solid-svg-icons';

export { default as ConvictionWidgetBuilder } from './ConvictionWidgetBuilder';
export { default as ConvictionWidgetRenderer } from './ConvictionWidgetRenderer';
export * from './types';

export const definition: WidgetDefinition = {
  type: 'conviction',
  meta: {
    label: 'Conviction Score',
    icon: React.createElement(FontAwesomeIcon, { icon: faBullseye }),
    description: 'Rate investment conviction with scoring system',
    isSystem: true,
    isCreatable: false,
    shareable: true,
    editableInWorkflow: true,
    dataFlow: 'input',
  },
  defaultSize: { w: 6, h: 5, minW: 4, minH: 4 },
  defaultConfig: () => ({
    showLabels: true,
    allowComments: true,
  } as ConvictionWidgetConfig),
  Builder: ConvictionWidgetBuilder,
  Renderer: ConvictionWidgetRenderer,
  Preview,
  validate: (data: unknown): string[] => {
    const convData = data as ConvictionDataContent | undefined;
    if (!convData?.categories?.length) return [];
    const errors: string[] = [];
    convData.categories.forEach(cat => {
      const unscored = cat.score === undefined || cat.score === null;
      // `required` defaults to true to preserve the prior all-required
      // behavior; an owner can set required:false to make a category optional.
      const required = cat.required !== false;
      if (unscored) {
        if (required) errors.push(`Category "${cat.name}" requires a score`);
        return; // optional + unscored: nothing else to check
      }
      // Bounds validation — minScore/maxScore were declared but never enforced.
      const min = cat.minScore ?? 1;
      const max = cat.maxScore ?? 10;
      if (cat.score! < min || cat.score! > max) {
        errors.push(`Category "${cat.name}" score must be between ${min} and ${max}`);
      }
    });
    return errors;
  },
};
